import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vorApi } from '../services/api';
import { toast } from 'react-toastify';

// ── Styles ────────────────────────────────────────────────────────────────────

const card = { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' };
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#374151' };
const btnPrimary = { padding: '10px 24px', backgroundColor: '#6B3FA0', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' };
const btnSecondary = { padding: '10px 20px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' };
const fmtCurrency = (val) => val != null ? `$${Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—';

const STEPS = [
  { label: 'Basic Info',        icon: '📋' },
  { label: 'Risk Tolerance',    icon: '🎯' },
  { label: 'Cost Inputs',       icon: '💰' },
  { label: 'Credible Risks',    icon: '⚠️' },
  { label: 'Controls',          icon: '🛡️' },
  { label: 'Results & Report',  icon: '📊' },
];

// ── Step 1: Basic Info ────────────────────────────────────────────────────────

const Step1 = ({ assessment, client }) => (
  <div>
    <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Basic Information</h2>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      {[
        ['Assessment Name', assessment?.assessment_name],
        ['Client', client?.name],
        ['Assessment Date', assessment?.assessment_date],
        ['Status', assessment?.status],
      ].map(([label, value]) => (
        <div key={label} style={{ padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #F3EEFA' }}>
          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
          <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>{value || '—'}</div>
        </div>
      ))}
    </div>
    <p style={{ marginTop: '20px', color: '#6B7280', fontSize: '14px' }}>
      Review the details above, then continue through the workflow to complete the Value of Risk assessment.
    </p>
  </div>
);

// ── Step 2: Risk Tolerance ────────────────────────────────────────────────────

const Step2 = ({ questions, answers, onChange }) => (
  <div>
    <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Risk Tolerance Questions</h2>
    <p style={{ margin: '0 0 20px', color: '#6B7280', fontSize: '14px' }}>
      Answer each question to determine the organization's risk tolerance level.
    </p>
    {questions.map((q) => (
      <div key={q.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 0', borderBottom: '1px solid #F3F4F6' }}>
        <input
          type="checkbox"
          id={`q-${q.id}`}
          checked={!!answers[q.id]}
          onChange={(e) => onChange(q.id, e.target.checked)}
          style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: '#6B3FA0', flexShrink: 0, cursor: 'pointer' }}
        />
        <label htmlFor={`q-${q.id}`} style={{ flex: 1, cursor: 'pointer' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{q.question_text}</div>
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>+{q.points_if_yes} points if yes</div>
        </label>
      </div>
    ))}
    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F3EEFA', borderRadius: '6px' }}>
      <strong>Current score: {questions.reduce((sum, q) => sum + (answers[q.id] ? (q.points_if_yes || 10) : 0), 0)}</strong>
      {' '}/ {questions.reduce((sum, q) => sum + (q.points_if_yes || 10), 0)} — Risk tolerance determined during calculation.
    </div>
  </div>
);

// ── Step 3: Cost Inputs ───────────────────────────────────────────────────────

const CostField = ({ label, name, value, onChange, prefix = '$', help }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={labelStyle}>{label}</label>
    <div style={{ position: 'relative' }}>
      {prefix && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280', fontSize: '14px' }}>{prefix}</span>}
      <input
        type="number" name={name} value={value || ''} onChange={onChange} min="0"
        style={{ ...inputStyle, paddingLeft: prefix ? '22px' : '12px' }}
      />
    </div>
    {help && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '3px' }}>{help}</div>}
  </div>
);

const Step3 = ({ costInputs, onChange }) => {
  const col = { padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #F3EEFA' };
  return (
    <div>
      <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Financial Cost Inputs</h2>
      <p style={{ margin: '0 0 20px', color: '#6B7280', fontSize: '14px' }}>
        These values drive the financial risk calculations. Use current estimates — they can be updated later.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>📉 Outage & Downtime</div>
          <CostField label="Annual Revenue" name="annual_revenue" value={costInputs.annual_revenue} onChange={onChange} />
          <CostField label="Estimated Outage Hours (per incident)" name="estimated_outage_hours" value={costInputs.estimated_outage_hours} onChange={onChange} prefix="" />
        </div>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>💸 Ransomware & Fraud</div>
          <CostField label="Avg Ransomware Demand" name="avg_ransomware_demand" value={costInputs.avg_ransomware_demand} onChange={onChange} />
          <CostField label="Avg Wire Transfer Fraud Amount" name="avg_wire_transfer_amount" value={costInputs.avg_wire_transfer_amount} onChange={onChange} />
          <CostField label="Avg Regulatory Incident Fine" name="avg_incident_fine" value={costInputs.avg_incident_fine} onChange={onChange} />
        </div>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>🔒 Data Breach</div>
          <CostField label="Number of PII Records" name="num_pii_records" value={costInputs.num_pii_records} onChange={onChange} prefix="" />
          <CostField label="Avg Fine Per PII Record" name="avg_pii_fine" value={costInputs.avg_pii_fine} onChange={onChange} />
        </div>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>🖥️ Infrastructure Recovery</div>
          <CostField label="Number of Servers" name="num_servers" value={costInputs.num_servers} onChange={onChange} prefix="" />
          <CostField label="Avg Server Recovery Cost" name="avg_server_recovery_cost" value={costInputs.avg_server_recovery_cost} onChange={onChange} />
          <CostField label="Number of Endpoints" name="num_endpoints" value={costInputs.num_endpoints} onChange={onChange} prefix="" />
          <CostField label="Avg Endpoint Recovery Cost" name="avg_endpoint_recovery_cost" value={costInputs.avg_endpoint_recovery_cost} onChange={onChange} />
        </div>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>📋 Compliance</div>
          <CostField label="Revenue % at Risk from Compliance" name="compliance_revenue_percent" value={costInputs.compliance_revenue_percent} onChange={onChange} prefix="%" help="e.g. 15 = 15%" />
          <CostField label="Annual Compliance Contracts ($)" name="annual_compliance_contracts" value={costInputs.annual_compliance_contracts} onChange={onChange} />
        </div>
        <div style={col}>
          <div style={{ fontWeight: '600', color: '#4A2B70', marginBottom: '14px', fontSize: '14px' }}>🤝 Client Reputation</div>
          <CostField label="Avg Annual Revenue Per Client" name="avg_client_revenue" value={costInputs.avg_client_revenue} onChange={onChange} />
          <CostField label="Number of Clients Expected to Lose" name="num_lost_clients" value={costInputs.num_lost_clients} onChange={onChange} prefix="" help="Existing clients likely to leave after a breach" />
          <CostField label="Number of Prospects Expected to Lose" name="num_lost_prospects" value={costInputs.num_lost_prospects} onChange={onChange} prefix="" help="Prospective clients lost due to reputational damage" />
        </div>
      </div>
    </div>
  );
};

// ── Step 4: Credible Risks ────────────────────────────────────────────────────

const Step4 = ({ availableRisks, selectedRisks, onToggle, onScoreChange }) => {
  const byCategory = availableRisks.reduce((acc, r) => {
    const cat = r.risk_category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  return (
    <div>
      <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Credible Risks</h2>
      <p style={{ margin: '0 0 20px', color: '#6B7280', fontSize: '14px' }}>
        Select risks that are credible threats to this organization. Rate their current likelihood and impact (1–5).
      </p>
      {Object.entries(byCategory).map(([cat, risks]) => (
        <div key={cat} style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#4A2B70', marginBottom: '8px', padding: '6px 10px', backgroundColor: '#F3EEFA', borderRadius: '4px' }}>
            {cat}
          </div>
          {risks.map((risk) => {
            const sel = selectedRisks[risk.id];
            return (
              <div key={risk.id} style={{ border: '1px solid', borderColor: sel ? '#6B3FA0' : '#E5E7EB', borderRadius: '6px', padding: '12px', marginBottom: '8px', backgroundColor: sel ? '#F9F5FF' : 'white' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input
                    type="checkbox" checked={!!sel} onChange={() => onToggle(risk)}
                    style={{ marginTop: '3px', width: '17px', height: '17px', accentColor: '#6B3FA0', flexShrink: 0, cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '14px', color: '#111827' }}>{risk.risk_name}</div>
                    {risk.description && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{risk.description}</div>}
                    {sel && (
                      <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                        {[
                          { label: 'Current Likelihood', key: 'current_likelihood' },
                          { label: 'Current Impact', key: 'current_impact' },
                          { label: 'Future Likelihood', key: 'future_likelihood' },
                          { label: 'Future Impact', key: 'future_impact' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>{label}</label>
                            <select
                              value={sel[key] || ''}
                              onChange={(e) => onScoreChange(risk.id, key, e.target.value)}
                              style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                            >
                              <option value="">—</option>
                              {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                        ))}
                        <div>
                          <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Score</label>
                          <div style={{ padding: '5px 0', fontSize: '14px', fontWeight: '700', color: '#6B3FA0' }}>
                            {sel.current_likelihood && sel.current_impact ? sel.current_likelihood * sel.current_impact : '—'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ── Step 5: Controls ──────────────────────────────────────────────────────────

const CATEGORY_ICONS = {
  'Hardware and Physical Controls': '🖥️',
  'Data Protection': '🔒',
  'Perimeter Defense': '🛡️',
  'Endpoint Protection': '💻',
  'Email Security': '📧',
  'Access Control': '🔑',
  'Monitoring and Detection': '👁️',
  'Policies and Procedures': '📋',
};

const Step5 = ({ availableControls, selectedControls, onToggle, onFieldChange }) => {
  const [mode, setMode] = useState('current');
  const byCategory = availableControls.reduce((acc, c) => {
    const cat = c.control_category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  return (
    <div>
      <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Security Controls</h2>
      <p style={{ margin: '0 0 16px', color: '#6B7280', fontSize: '14px' }}>
        Select current controls in place, then select recommended controls to add.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[['current', '✅ Current Controls'], ['recommended', '⭐ Recommended Controls']].map(([key, label]) => (
          <button key={key} onClick={() => setMode(key)} style={{
            padding: '8px 18px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px',
            backgroundColor: mode === key ? '#6B3FA0' : 'white',
            color: mode === key ? 'white' : '#374151',
            border: `1px solid ${mode === key ? '#6B3FA0' : '#D1D5DB'}`,
          }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
        {mode === 'current'
          ? 'Check each control that is currently implemented.'
          : 'Check controls you recommend implementing. Optionally enter implementation and annual costs.'}
      </div>

      {Object.entries(byCategory).map(([cat, controls]) => (
        <div key={cat} style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#4A2B70', marginBottom: '6px', padding: '6px 10px', backgroundColor: '#F3EEFA', borderRadius: '4px' }}>
            {CATEGORY_ICONS[cat] || '🔧'} {cat}
          </div>
          {controls.map((ctrl) => {
            const sel = selectedControls[ctrl.id];
            const isAlreadyCurrent = mode === 'recommended' && sel?.is_current;
            if (isAlreadyCurrent) return null;
            const isChecked = mode === 'current' ? sel?.is_current : sel?.is_recommended;
            return (
              <div key={ctrl.id} style={{ border: '1px solid', borderColor: isChecked ? '#6B3FA0' : '#E5E7EB', borderRadius: '6px', padding: '10px 12px', marginBottom: '6px', backgroundColor: isChecked ? '#F9F5FF' : 'white' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox" checked={!!isChecked}
                    onChange={() => onToggle(ctrl, mode)}
                    style={{ width: '16px', height: '16px', accentColor: '#6B3FA0', flexShrink: 0, cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#111827' }}>{ctrl.control_name}</span>
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9CA3AF' }}>{ctrl.effectiveness_points}pt</span>
                  </div>
                  {mode === 'recommended' && isChecked && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#6B7280', display: 'block' }}>Impl. Cost</label>
                        <input
                          type="number" value={sel?.implementation_cost || ''} min="0"
                          onChange={(e) => onFieldChange(ctrl.id, 'implementation_cost', e.target.value)}
                          style={{ width: '110px', padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                          placeholder="$0"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#6B7280', display: 'block' }}>Annual Cost</label>
                        <input
                          type="number" value={sel?.annual_cost || ''} min="0"
                          onChange={(e) => onFieldChange(ctrl.id, 'annual_cost', e.target.value)}
                          style={{ width: '110px', padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
                          placeholder="$0"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ── Step 6: Results ───────────────────────────────────────────────────────────

const Step6 = ({ assessmentId, results, clientName, onCalculate, calculating }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await vorApi.downloadReport(assessmentId, clientName);
      toast.success('Report downloaded');
    } catch {
      toast.error('Report generation failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!results) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600' }}>Run the Calculation</h2>
        <p style={{ color: '#6B7280', marginBottom: '24px' }}>
          All inputs have been saved. Click below to calculate the Value of Risk.
        </p>
        <button onClick={onCalculate} disabled={calculating} style={{ ...btnPrimary, opacity: calculating ? 0.7 : 1 }}>
          {calculating ? 'Calculating…' : '⚡ Calculate VoR'}
        </button>
      </div>
    );
  }

  const implementationCost = 0; // will come from controls data
  const roi = results.risk_reduction_value > 0 && implementationCost > 0
    ? (results.risk_reduction_value / implementationCost).toFixed(1)
    : null;

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>Results & Report</h2>

      {/* Key numbers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Risk Tolerance Level', value: results.risk_tolerance_level, color: '#4A2B70' },
          { label: 'Baseline Risk Exposure', value: fmtCurrency(results.baseline_risk_value), color: '#DC2626' },
          { label: 'Current Risk (with controls)', value: fmtCurrency(results.total_current_risk_value), color: '#D97706' },
          { label: 'Future Risk (+ recommended)', value: fmtCurrency(results.total_future_risk_value), color: '#059669' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #F3EEFA' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Value proposition */}
      <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', color: '#065F46', fontWeight: '600', marginBottom: '4px' }}>RISK REDUCTION VALUE</div>
        <div style={{ fontSize: '36px', fontWeight: '800', color: '#059669' }}>{fmtCurrency(results.risk_reduction_value)}</div>
        <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '6px' }}>
          Current controls reduce baseline by {Number(results.current_control_reduction_percentage || 0).toFixed(1)}% ·
          Recommended controls add {Number(results.recommended_control_reduction_percentage || 0).toFixed(1)}% more
        </div>
      </div>

      {/* Breakdown */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5D5F5', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '12px 16px', backgroundColor: '#F3EEFA', fontWeight: '600', fontSize: '14px', color: '#4A2B70' }}>
          Risk Breakdown
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Business Interruption', results.total_business_interruption_cost],
              ['Financial Loss Exposure', results.total_financial_loss_exposure],
              ['Recovery Costs', results.total_recovery_cost],
              ['Compliance Risk', results.total_compliance_risk],
              ['Reputation Risk', results.total_reputation_risk],
            ].map(([label, val], i) => (
              <tr key={label} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                <td style={{ padding: '10px 16px', fontSize: '14px', color: '#374151' }}>{label}</td>
                <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '500', textAlign: 'right' }}>{fmtCurrency(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button onClick={onCalculate} disabled={calculating}
          style={{ ...btnSecondary, opacity: calculating ? 0.7 : 1 }}>
          {calculating ? 'Recalculating…' : '🔄 Recalculate'}
        </button>
        <button onClick={handleDownload} disabled={downloading}
          style={{ ...btnPrimary, backgroundColor: '#059669', opacity: downloading ? 0.7 : 1 }}>
          {downloading ? 'Generating…' : '📄 Download Word Report'}
        </button>
      </div>
    </div>
  );
};

// ── Main Workflow Component ────────────────────────────────────────────────────

const VorAssessmentWorkflow = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Data state
  const [assessment, setAssessment] = useState(null);
  const [client, setClient] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [rtAnswers, setRtAnswers] = useState({});
  const [costInputs, setCostInputs] = useState({});
  const [availableRisks, setAvailableRisks] = useState([]);
  const [selectedRisks, setSelectedRisks] = useState({});
  const [availableControls, setAvailableControls] = useState([]);
  const [selectedControls, setSelectedControls] = useState({});
  const [results, setResults] = useState(null);

  // Load everything on mount
  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        const [assessRes, qRes, riskRes, ctrlRes] = await Promise.all([
          vorApi.getAssessment(assessmentId),
          vorApi.listQuestions(),
          vorApi.listRisks(),
          vorApi.listControls(),
        ]);

        const d = assessRes.data;
        setAssessment(d.assessment);
        setClient(d.client);
        setResults(d.results || null);
        setQuestions(qRes.data.questions || []);
        setAvailableRisks(riskRes.data.risks || []);
        setAvailableControls(ctrlRes.data.controls || []);

        // Restore saved state
        const savedAnswers = {};
        (d.risk_tolerance || []).forEach(a => { savedAnswers[a.question_id] = !!a.answer; });
        setRtAnswers(savedAnswers);

        setCostInputs(d.cost_inputs || {});

        const allRisks = riskRes.data.risks || [];
        const savedRisks = {};
        if ((d.credible_risks || []).length > 0) {
          (d.credible_risks || []).forEach(r => {
            savedRisks[r.risk_id] = {
              is_credible: r.is_credible,
              current_likelihood: r.current_likelihood,
              current_impact: r.current_impact,
              future_likelihood: r.future_likelihood,
              future_impact: r.future_impact,
            };
          });
        } else {
          // New assessment: pre-select all catalog risks except TEC-004
          allRisks.forEach(risk => {
            if (risk.risk_code !== 'TEC-004') {
              savedRisks[risk.id] = {
                is_credible: true,
                current_likelihood: risk.default_likelihood || 3,
                current_impact: risk.default_impact || 3,
                future_likelihood: risk.default_likelihood || 3,
                future_impact: risk.default_impact || 3,
              };
            }
          });
        }
        setSelectedRisks(savedRisks);

        const savedControls = {};
        (d.controls || []).forEach(c => {
          savedControls[c.control_id] = {
            is_current: !!c.is_current_control,
            is_recommended: !!c.is_recommended_control,
            implementation_cost: c.implementation_cost,
            annual_cost: c.annual_cost,
          };
        });
        setSelectedControls(savedControls);

        // Jump to furthest completed step
        // Use annual_revenue > 0 as "cost inputs done" signal (not estimated_outage_hours which has a non-zero default)
        if (d.results) setCurrentStep(6);
        else if ((d.controls || []).length > 0) setCurrentStep(6);
        else if ((d.credible_risks || []).length > 0) setCurrentStep(5);
        else if ((d.cost_inputs?.annual_revenue || 0) > 0) setCurrentStep(4);
        else if ((d.risk_tolerance || []).length > 0) setCurrentStep(3);
        else setCurrentStep(2); // Step 1 is read-only info, always start at Risk Tolerance

      } catch {
        toast.error('Failed to load assessment');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [assessmentId]);

  // ── Step handlers ────────────────────────────────────────────────────────

  const saveStep2 = async () => {
    const answers = Object.entries(rtAnswers).map(([qid, ans]) => ({ question_id: Number(qid), answer: ans ? 1 : 0 }));
    await vorApi.saveRiskTolerance(assessmentId, { answers });
  };

  const saveStep3 = async () => {
    await vorApi.saveCostInputs(assessmentId, costInputs);
  };

  const saveStep4 = async () => {
    const risks = Object.entries(selectedRisks).map(([risk_id, r]) => ({
      risk_id: Number(risk_id), is_credible: true,
      current_likelihood: r.current_likelihood, current_impact: r.current_impact,
      future_likelihood: r.future_likelihood, future_impact: r.future_impact,
    }));
    await vorApi.saveCredibleRisks(assessmentId, { risks });
  };

  const saveStep5 = async () => {
    const controls = Object.entries(selectedControls)
      .filter(([, c]) => c.is_current || c.is_recommended)
      .map(([control_id, c]) => ({
        control_id: Number(control_id),
        is_current_control: c.is_current,
        is_recommended_control: c.is_recommended,
        implementation_cost: c.implementation_cost || null,
        annual_cost: c.annual_cost || null,
      }));
    await vorApi.saveControls(assessmentId, { controls });
  };

  const handleNext = async () => {
    try {
      setSaving(true);
      if (currentStep === 2) await saveStep2();
      if (currentStep === 3) await saveStep3();
      if (currentStep === 4) await saveStep4();
      if (currentStep === 5) await saveStep5();
      setCurrentStep(s => Math.min(s + 1, 6));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      const res = await vorApi.calculate(assessmentId);
      setResults(res.data.results);
      toast.success('Calculation complete');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  // ── Risk / Control helpers ────────────────────────────────────────────────

  const toggleRisk = useCallback((risk) => {
    setSelectedRisks(prev => {
      if (prev[risk.id]) {
        const next = { ...prev };
        delete next[risk.id];
        return next;
      }
      return {
        ...prev,
        [risk.id]: {
          is_credible: true,
          current_likelihood: risk.default_likelihood || 3,
          current_impact: risk.default_impact || 3,
          future_likelihood: risk.default_likelihood || 3,
          future_impact: risk.default_impact || 3,
        },
      };
    });
  }, []);

  const updateRiskScore = useCallback((riskId, key, value) => {
    setSelectedRisks(prev => ({ ...prev, [riskId]: { ...prev[riskId], [key]: Number(value) } }));
  }, []);

  const toggleControl = useCallback((ctrl, mode) => {
    setSelectedControls(prev => {
      const existing = prev[ctrl.id] || {};
      const field = mode === 'current' ? 'is_current' : 'is_recommended';
      return { ...prev, [ctrl.id]: { ...existing, [field]: !existing[field] } };
    });
  }, []);

  const updateControlField = useCallback((ctrlId, field, value) => {
    setSelectedControls(prev => ({ ...prev, [ctrlId]: { ...prev[ctrlId], [field]: value } }));
  }, []);

  const handleCostChange = (e) => {
    const { name, value } = e.target;
    setCostInputs(prev => ({ ...prev, [name]: value }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>Loading…</div>;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '20px', fontSize: '13px', color: '#6B7280' }}>
        <Link to="/vor/assessments" style={{ color: '#6B3FA0', textDecoration: 'none' }}>VoR Assessments</Link>
        {' / '}
        <Link to={`/vor/assessments/${assessmentId}`} style={{ color: '#6B3FA0', textDecoration: 'none' }}>
          {assessment?.assessment_name}
        </Link>
        {' / Workflow'}
      </div>

      <h1 style={{ margin: '0 0 20px', fontSize: '24px', fontWeight: '700', color: '#1a1a2e' }}>
        {assessment?.assessment_name}
        <span style={{ marginLeft: '12px', fontSize: '14px', color: '#6B7280', fontWeight: 'normal' }}>
          {client?.name}
        </span>
      </h1>

      {/* Step indicator */}
      <div style={{ ...card, padding: '16px 24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          {STEPS.map((step, i) => {
            const stepNum = i + 1;
            const done = stepNum < currentStep;
            const active = stepNum === currentStep;
            return (
              <div key={step.label} style={{ flex: 1, textAlign: 'center', position: 'relative', cursor: done ? 'pointer' : 'default' }}
                onClick={() => done && setCurrentStep(stepNum)}>
                {i < STEPS.length - 1 && (
                  <div style={{ position: 'absolute', top: '15px', left: '50%', right: '-50%', height: '2px', backgroundColor: done ? '#6B3FA0' : '#E5D5F5', zIndex: 0 }} />
                )}
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%', margin: '0 auto 6px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1,
                  backgroundColor: active ? '#6B3FA0' : done ? '#4A2B70' : '#F3EEFA',
                  color: (active || done) ? 'white' : '#9CA3AF',
                  fontSize: '12px', fontWeight: '700',
                }}>
                  {done ? '✓' : stepNum}
                </div>
                <div style={{ fontSize: '10px', color: active ? '#6B3FA0' : done ? '#4A2B70' : '#9CA3AF', fontWeight: active || done ? '600' : 'normal' }}>
                  {step.icon} {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div style={{ ...card, padding: '28px', marginBottom: '20px', minHeight: '400px' }}>
        {currentStep === 1 && <Step1 assessment={assessment} client={client} />}
        {currentStep === 2 && (
          <Step2
            questions={questions}
            answers={rtAnswers}
            onChange={(qid, val) => setRtAnswers(p => ({ ...p, [qid]: val }))}
          />
        )}
        {currentStep === 3 && <Step3 costInputs={costInputs} onChange={handleCostChange} />}
        {currentStep === 4 && (
          <Step4
            availableRisks={availableRisks}
            selectedRisks={selectedRisks}
            onToggle={toggleRisk}
            onScoreChange={updateRiskScore}
          />
        )}
        {currentStep === 5 && (
          <Step5
            availableControls={availableControls}
            selectedControls={selectedControls}
            onToggle={toggleControl}
            onFieldChange={updateControlField}
          />
        )}
        {currentStep === 6 && (
          <Step6
            assessmentId={assessmentId}
            results={results}
            clientName={client?.name}
            onCalculate={handleCalculate}
            calculating={calculating}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          {currentStep > 1 && (
            <button onClick={() => setCurrentStep(s => s - 1)} style={btnSecondary}>
              ← Previous
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate(`/vor/assessments/${assessmentId}`)} style={btnSecondary}>
            View Details
          </button>
          {currentStep < 6 && (
            <button onClick={handleNext} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save & Next →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VorAssessmentWorkflow;
